import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { ViewSettings } from '../../../../../viewer/view-settings';
import { useUnitSymbols } from '../../../../hooks/use-unit-symbols';
import { CheckBox } from '../../../check-box/check-box';
import { Icon } from '../../../icon/icon';
import {
  ChoiceLabel,
  ChoicePicker,
  Hint,
  RemoveSpace,
  RowEnd,
  RowLabel,
  RowStart,
  SheetHeader,
  SheetRow,
  SnappingHelp,
  useHelp,
  ValueField,
} from './snapping-sheet';

type Target = 'pointCloud' | 'tiledModel' | 'assets';
type HeightMode = 'absolute' | 'offset';

type Props = {
  pointCloud: boolean;
  tiledModel: boolean;
  assets: boolean;
  onToggle: (target: Target) => void;
  /** Whether some assets are not snapped towards, see SnapAssetsDialog. */
  customAssets: boolean;
  onChooseAssets: () => void;
  /** The height setting, off when undefined, and its values. */
  height: HeightMode | undefined;
  absoluteHeight: number;
  heightOffset: number;
  onHeightChanged: (height: HeightMode | undefined) => void;
  onAbsoluteHeightChanged: (value: number) => void;
  onHeightOffsetChanged: (value: number) => void;
};

/**
 * What is snapped towards: the point cloud, the 3D model, the objects and a height, each a row
 * with a checkbox. The height shows its choice and value while it is on, and clicking its choice
 * opens the choices. Without the point cloud and the 3D model in the CAD viewer.
 */
export const SnapToSection = (props: Props) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  const [isCadViewer] = React.useState(ViewSettings.instance.isCadViewer);
  // Remembered so that the height is turned on again with the last choice.
  const [lastHeight, setLastHeight] = React.useState<HeightMode>(props.height ?? 'absolute');
  const [picking, setPicking] = React.useState(false);

  const heightChoices: Record<HeightMode, SnappingHelp & { title: string }> = {
    absolute: {
      title: t('section.snapping.height.absolute.text', { ns: 'skyviewDesign' }),
      text: t('section.snapping.height.absolute.tooltip', { ns: 'skyviewDesign' }),
    },
    offset: {
      title: t('section.snapping.height.offset.text', { ns: 'skyviewDesign' }),
      text: t('section.snapping.height.offset.tooltip', { ns: 'skyviewDesign' }),
    },
  };

  const setHeight = (height: HeightMode | undefined) => {
    if (isDefined(height)) {
      setLastHeight(height);
    }
    props.onHeightChanged(height);
  };

  const target = (key: Target, label: string, help: string, end?: React.ReactNode) => (
    <TargetRow
      checked={props[key]}
      end={end}
      help={{ title: label, text: help }}
      label={label}
      onChange={() => props.onToggle(key)}
    />
  );

  const heightTitle = t('section.snapping.height.text', { ns: 'skyviewDesign' });
  // Höjd, or Z-offset, also while it is off, as it is turned on again with the last choice
  const heightLabel =
    (props.height ?? lastHeight) === 'offset' ? heightChoices.offset.title : heightTitle;
  const choice = props.height && heightChoices[props.height];

  return (
    <>
      <SheetHeader
        info={t('section.snapping.snapToInfo', { ns: 'skyviewDesign' })}
        title={t('section.snapping.snapTo', { ns: 'skyviewDesign' })}
      />
      {!isCadViewer && (
        <>
          {target(
            'pointCloud',
            t('snap.pointCloud', { ns: 'skyviewDesign' }),
            t('snappingDialog.help.pointCloud', { ns: 'skyviewDesign' }),
          )}
          {target(
            'tiledModel',
            t('snappingDialog.tiledModel', { ns: 'skyviewDesign' }),
            t('snappingDialog.help.tiledModel', { ns: 'skyviewDesign' }),
            <Hint>{t('snappingDialog.timeline', { ns: 'skyviewDesign' })}</Hint>,
          )}
        </>
      )}
      {target(
        'assets',
        t('snappingDialog.assets', { ns: 'skyviewDesign' }),
        t('snappingDialog.help.assets', { ns: 'skyviewDesign' }),
        <>
          {props.customAssets && (
            <Custom>{t('section.snapping.snapAssets.custom', { ns: 'skyviewDesign' })}</Custom>
          )}
          <Icon
            aria-label={t('section.snapping.snapAssets.settings', { ns: 'skyviewDesign' })}
            fixedWidth={true}
            icon={['fal', 'cog']}
            title={t('section.snapping.snapAssets.settings', { ns: 'skyviewDesign' })}
            onClick={props.onChooseAssets}
            onHoverStyle={{ icon: ['fas', 'cog'] }}
          />
        </>,
      )}

      <SheetRow $off={!choice}>
        <RowStart>
          {choice ? (
            <>
              <CheckBox
                aria-label={heightLabel}
                checked={true}
                onChange={() => setHeight(undefined)}
              />
              <ChoiceLabel
                choice={choice.title}
                expanded={picking}
                help={choice}
                // Höjd, or Z-offset, rather than both the setting and its choice
                label={heightLabel}
                setting={heightTitle}
                onClick={() => setPicking(!picking)}
              />
            </>
          ) : (
            <TargetCheck
              checked={false}
              help={{ title: heightLabel, text: heightChoices[lastHeight].text }}
              label={heightLabel}
              onChange={() => setHeight(lastHeight)}
            />
          )}
        </RowStart>
        <RowEnd>
          {props.height === 'absolute' && (
            <ValueField
              defaultValue={props.absoluteHeight}
              title={t('section.snapping.height.absolute.inputTitle', { ns: 'skyviewDesign' })}
              unit={unitSymbols.length}
              onChange={props.onAbsoluteHeightChanged}
            />
          )}
          {props.height === 'offset' && (
            <ValueField
              defaultValue={props.heightOffset}
              title={t('section.snapping.height.offset.inputTitle', { ns: 'skyviewDesign' })}
              unit={unitSymbols.length}
              onChange={props.onHeightOffsetChanged}
            />
          )}
          {/* In line with the values of the lines and offsets, which can be removed */}
          {choice && <RemoveSpace />}
        </RowEnd>
      </SheetRow>
      {picking && choice && (
        <ChoicePicker
          rows={[
            {
              name: heightTitle,
              active: true,
              choices: (['absolute', 'offset'] as const).map((mode) => ({
                ...heightChoices[mode],
                active: props.height === mode,
                help: heightChoices[mode],
                onPick: () => {
                  setHeight(mode);
                  setPicking(false);
                },
              })),
            },
          ]}
          title={heightTitle}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
};

/**
 * A checkbox and its label, which shows its help in the band.
 */
const TargetCheck = (props: {
  checked: boolean;
  label: string;
  help: SnappingHelp;
  onChange: () => void;
}) => (
  <CheckRow {...useHelp(props.help)}>
    <CheckBox checked={props.checked} onChange={props.onChange}>
      <RowLabel as="span">{props.label}</RowLabel>
    </CheckBox>
  </CheckRow>
);

const TargetRow = (props: {
  checked: boolean;
  label: string;
  help: SnappingHelp;
  end?: React.ReactNode;
  onChange: () => void;
}) => (
  <SheetRow $off={!props.checked}>
    <RowStart>
      <TargetCheck
        checked={props.checked}
        help={props.help}
        label={props.label}
        onChange={props.onChange}
      />
    </RowStart>
    <RowEnd>
      {props.end}
      <RemoveSpace />
    </RowEnd>
  </SheetRow>
);

const CheckRow = styled.span`
  min-width: 0;
`;

const Custom = styled.i`
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.9em;
  white-space: nowrap;
`;
